import React, { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Video } from "@/components/common/video";
import { Image } from "@/components/common/image";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ModalCloseButton } from "@/components/common/modal-close-button";
import { DownloadIcon, MoreIcon, TrashIcon } from "@/components/ui/icon";
import AgentVideoController from "./agent-video-controller";
import { TruncatedText } from "../common/truncated-text";
import { useDownload } from "@/hooks/use-download";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { AgentShareModal } from "../agent-share-modal";
import { PublishIcon } from "@/components/agent-share-modal/assets";
import { TextButton } from "@/components/common/text-button";
import { IconButton } from "@/components/common/icon-button";
import { MoreActionItem, MoreActionPopover } from "@/components/common/more-action-popover";
import { Edit3, RefreshCcw } from "lucide-react";
import { ConfirmModal } from "@/components/common/confirm-modal";
import { HashTagText } from "../common/hash-tag-text";
import { AccountCard } from "../common/account-card";
import { AiCreationTypeIcon } from "../agent-creation/assets/ai-creation-icon";
import { InfoCircleIcon } from "../agent-creation/assets/info-circle-icon";
import { GenerateActorButton, SaveToMaterialButton, UseReferenceButton } from "../agent-creation/message";
import dayjs from "dayjs";
import { ImageHoverPreview } from "../common/image-hover-preview";
import { DynamicPopover } from "../common/dynamic-popover";
import { getModelNameByValue } from "../agent-creation/constants/ai-creation-model";
import ImageStack from "../common/image-stack";
import { cn } from "@/lib/utils";

export interface AgentAiCreationVideoModalProps {
  open: boolean;
  // 视频源与封面
  videoUrl: string;
  poster?: string;
  text: string;
  model?: string;
  imageSize?: string;
  duration?: number;
  aspectRatio?: string;
  deleteLoading?: boolean;
  isAutoPush?: boolean;
  inputAddress?: string;

  // 操作
  onOpenChange: (open: boolean) => void;
  onDownload?: (videoUrl: string) => void;
  onShare?: (videoUrl: string) => void;
  onReedit?: () => void;
  onDelete?: () => Promise<void>;
  onRegenerate?: () => void;
}

export const AgentAiCreationVideoModal: React.FC<AgentAiCreationVideoModalProps> = ({
  open,
  onOpenChange,
  videoUrl,
  poster,
  model,
  inputAddress,
  duration,
  imageSize,
  aspectRatio,
  text,
  deleteLoading,
  isAutoPush,
  onDownload,
  onShare,
  onReedit,
  onDelete,
  onRegenerate,
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const { download } = useDownload();
  const { toast } = useToast();

  const handleDownload = async () => {
    try {
      if (onDownload) {
        await Promise.resolve(onDownload(videoUrl));
        toast.default({ description: t('agent_creation.agent_modal.download_complete') });
        return;
      }
      await download(videoUrl, "video");
      toast.default({ description: t('agent_creation.agent_modal.download_complete') });
    } catch {
      toast.default({ description: t('agent_creation.agent_modal.download_fail') });
    }
  };
  // 响应式宽度状态
  const [leftWidth, setLeftWidth] = useState<number>(498);
  const [windowWidth, setWindowWidth] = useState<number>(0);


  // 判断是否为横屏比例
  const isLandscapeRatio = (ratio?: string) => {
    if (!ratio) return false;
    const normalized = ratio.replace(':', '/');
    return ['4/3', '16/9', '21/9'].includes(normalized);
  };
  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // 初始设置
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 根据窗口宽度和比例计算左侧宽度
  useEffect(() => {
    const isLandscape = isLandscapeRatio(aspectRatio);
    let baseWidth = isLandscape ? 610 : 498;

    if (windowWidth >= 2100) {
      baseWidth = isLandscape ? 960 : 667;
    }

    setLeftWidth(baseWidth);
  }, [windowWidth, aspectRatio]);

  const getAspectStyle = () => {
    if (!aspectRatio) return { aspectRatio: '9/16', maxWidth: `${leftWidth}px` }; // 默认比例
    return { aspectRatio: aspectRatio?.replace(':', '/'), maxWidth: `${leftWidth}px` };
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[24px] shadow-lg z-50 flex h-[90vh]"
        >
          {/* 左侧：视频区不设固定宽度，随视频自动撑开 */}
          <div
            className="relative group bg-black flex items-center justify-center flex-shrink-0 flex-grow-0 h-[90vh] aspect-[3/4] max-w-[422px]"
            style={getAspectStyle()}
          >
            {/* 视频容器 */}
            <Video
              ref={videoRef}
              src={videoUrl}
              // className="h-full w-auto"
              className={cn(
                "w-full h-full object-contain",
                aspectRatio === '9:16' && 'object-contain'
              )}
              poster={poster}
              autoPlay
              preload="auto"
              controls={false}
            />

            {/* 播放控制条 */}
            <div className="absolute bottom-0 left-0 right-0 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-200">
              <AgentVideoController videoRef={videoRef} />
            </div>
          </div>

          <div className="p-6 w-[430px] h-full bg-white text-[rgba(56,65,74,1)] flex flex-col shrink-0 grow-0 gap-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <AiCreationTypeIcon className="size-6" />
                <span className="text-base font-medium text-[rgba(16,24,40,1)]">AI创作</span>
              </div>
              <ModalCloseButton
                onClick={() => onOpenChange(false)}
              />
            </div>
            {/* 内容区域 */}
            <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-x-hidden overflow-y-auto">
              <label className="text-sm text-[rgba(102,112,133,1)]">脚本</label>
              <div className="text-sm text-[rgba(51,51,51,1)]">
                {text}
              </div>
              <div className="flex items-center">
                {inputAddress && <>
                  <ImageStack images={inputAddress.split(",")} />
                  <span className="text-sm text-[rgba(102,112,133,1)] ml-2">参考图</span>
                  <span className='w-[1px] h-[14px] bg-[rgba(208,213,221,1)] mx-[9.5px]' />
                </>}
                <div className='flex items-center'>
                  {model && <span className='text-sm text-[rgba(102,112,133,1)]'>{getModelNameByValue(model)}</span>}

                  {aspectRatio && <span className=' w-[1px] h-[14px] bg-[rgba(208,213,221,1)] mx-[9.5px]' />}
                  {aspectRatio && <span className='text-sm text-[rgba(102,112,133,1)]'>{aspectRatio}</span>}

                  {imageSize && <span className=' w-[1px] h-[14px] bg-[rgba(208,213,221,1)] mx-[9.5px]' />}
                  {imageSize && <span className='text-sm text-[rgba(102,112,133,1)]'>{imageSize}</span>}

                  {duration && <span className=' w-[1px] h-[14px] bg-[rgba(208,213,221,1)] mx-[9.5px]' />}
                  {duration && <span className='text-sm text-[rgba(102,112,133,1)]'>{duration}s</span>}

                  <span className=' w-[1px] h-[14px] bg-[rgba(208,213,221,1)] mx-[9.5px]' />
                  <DynamicPopover
                    side="bottom"
                    align="center"
                    mode="hover"
                    triggerHidden={false}
                    className="rounded-[16px] bg-[rgba(255,255,255,1)] [box-shadow:0px_0px_12px_rgba(16,_24,_40,_0.08)] p-4 border-none"
                    trigger={
                      <div className="cursor-pointer">
                        <InfoCircleIcon className="align-middle text-[rgba(102,112,133,1)] hover:text-[rgba(51,51,51,1)] transition-colors" />
                      </div>
                    }
                  >
                    <div className="flex items-center gap-2 ">
                      <span className="text-sm text-[rgba(102,112,133,1)]">生成时间</span>
                      <span className="text-sm text-[rgba(51,51,51,1)]">{dayjs(new Date()).format('YYYY-MM-DD HH:mm')}</span>
                    </div>
                  </DynamicPopover>
                </div>
              </div>
            </div>
            {/* 操作按钮区域 */}
            <div className="flex w-full gap-3 justify-end items-end flex-shrink-0 mt-auto">
              <MoreActionPopover
                className="w-40"
                side="top"
                align="center"
                tiggerClassName="size-9 rounded-[8px]"
                tiggerIcon={
                  <IconButton
                    icon={<MoreIcon className="size-5 !text-[rgba(51,51,51,1)]" />}
                  />
                }
              >
                <MoreActionItem
                  icon={<Edit3 className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                  className='text-[rgba(16,24,40,1)]'
                  label={t("agent_creation.content.reedit")}
                  onClick={() => {
                    onReedit?.();
                    onOpenChange(false);
                  }}
                />

                <MoreActionItem
                  icon={<RefreshCcw className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                  className='text-[rgba(16,24,40,1)]'
                  label={t("agent_creation.content.regenerate_again")}
                  onClick={() => {
                    onRegenerate?.();
                    onOpenChange(false);
                  }}
                />
                <MoreActionItem
                  icon={<DownloadIcon className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                  className='text-[rgba(16,24,40,1)]'
                  label={t("common.download")}
                  onClick={() => {
                    handleDownload();
                  }}
                />
                <ConfirmModal
                  trigger={
                    <MoreActionItem
                      icon={<TrashIcon className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                      className='text-[rgba(16,24,40,1)]'
                      label={t("common.delete")}
                    />
                  }
                  title={t('agent_creation.content.delete_title')}
                  description={t('agent_creation.content.delete_desc')}
                  cancel={t('common.cancel')}
                  confirm={t('common.delete')}
                  onConfirm={async () => {
                    await onDelete?.();
                    onOpenChange(false);
                  }}
                  loading={deleteLoading}
                />
              </MoreActionPopover>
              <SaveToMaterialButton
                className="size-9 rounded-[8px]"
                iconClassName="size-5"
                url={videoUrl}
              />
              <GenerateActorButton
                type="video"
                fileUrl={videoUrl}
                className="size-9 rounded-[8px]"
                iconClassName="size-5"
              />
              <AgentShareModal
                shouldFetchData={open}
                showDouyin
                douyinOptions={{
                  videoUrls: [videoUrl],
                }}
                showTiktok
                tiktokOptions={{
                  videoUrls: [videoUrl],
                }}
                trigger={
                  <TextButton
                    className="px-[22px] py-[10px] rounded-[8px] max-w-max text-sm"
                    icon={<PublishIcon className="w-5 h-5" />}
                    label={t('agent_creation.agent_modal.publish')}
                  />
                }
              />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AgentAiCreationVideoModal;
